<template>
  <div class="journey-board">
    <div class="journey-board__toolbar">
      <div class="journey-board__filters" role="tablist">
        <button
          v-for="option in ballOptions"
          :key="option.value"
          type="button"
          role="tab"
          class="journey-board__filter"
          :class="{ 'journey-board__filter--active': ballFilter === option.value }"
          :aria-selected="ballFilter === option.value"
          @click="ballFilter = option.value"
        >
          <BaseIcon v-if="option.icon" :name="option.icon" size="14" />
          {{ option.label }}
          <span class="journey-board__filter-count">{{ option.count }}</span>
        </button>
      </div>
      <span v-if="loading" class="journey-board__loading">{{ $t('common.loading') }}</span>
    </div>

    <div class="journey-board__scroller">
      <div class="journey-board__columns">
        <section
          v-for="column in columns"
          :key="column.key"
          class="journey-board__column"
          :style="{ backgroundColor: column.background }"
        >
          <header class="journey-board__column-header">
            <div class="journey-board__column-title">
              <span
                class="journey-board__phase-badge"
                :style="{ backgroundColor: column.color, color: column.textColor }"
              >
                {{ $t(`journey.phases.${column.key}`) }}
              </span>
              <span class="journey-board__column-value">{{ formatCurrency(column.value) }}</span>
            </div>
            <span class="journey-board__column-count">{{ column.deals.length }}</span>
          </header>

          <div class="journey-board__body">
            <div class="journey-board__list">
              <div
                v-for="deal in column.visibleDeals"
                :key="deal.id"
                role="button"
                tabindex="0"
                class="journey-board__card"
                @click="$emit('card-click', deal)"
                @keydown.enter.self="$emit('card-click', deal)"
              >
                <div class="journey-board__heading">
                  <span class="journey-board__name">{{ dealName(deal) }}</span>
                  <span class="journey-board__amount">{{ formatCurrency(deal.value) }}</span>
                </div>

                <div class="journey-board__status">
                  <DealOwnerPicker
                    :owner="deal.owner"
                    :owner-id="deal.owner_id || deal.owner?.id || null"
                    :members="members"
                    @select="(ownerId) => $emit('assign-owner', deal, ownerId)"
                  />
                  <span
                    class="journey-board__signal"
                    :class="`journey-board__signal--${signalTone(deal)}`"
                    :title="$t(`journey.ball.${deal.journey.ball}`)"
                  >
                    <BaseIcon :name="ballIcons[deal.journey.ball]" size="14" />
                    <span class="journey-board__signal-text">
                      {{ stepLabel(deal.journey.next_step) }}
                      <template v-if="waitingDays(deal) > 0">
                        · {{ $t('deals.age_days', { count: waitingDays(deal) }) }}
                      </template>
                    </span>
                  </span>
                </div>

                <div v-if="isProjectBoard" class="journey-board__stage">
                  <span class="journey-board__stage-name">{{ deal.project.project_number }}</span>
                </div>
                <div v-else-if="deal.stage" class="journey-board__stage">
                  <span
                    class="journey-board__stage-dot"
                    :style="{ backgroundColor: deal.stage.color || '#434f6f' }"
                  />
                  <span class="journey-board__stage-name">{{ deal.stage.name }}</span>
                </div>
              </div>

              <button
                v-if="column.deals.length > column.visibleDeals.length"
                type="button"
                class="journey-board__more"
                @click="showMore(column.key)"
              >
                {{
                  $t('journey.board.show_more', {
                    count: column.deals.length - column.visibleDeals.length,
                  })
                }}
              </button>
            </div>

            <p v-if="!column.deals.length" class="journey-board__empty">
              {{ $t('journey.board.empty_column') }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, getCurrentInstance } from 'vue';
  import { useDealStore } from '@/store/dealStore';
  import { dealService } from '@/services/dealService';
  import { projectService } from '@/services/projectService';
  import {
    JOURNEY_BALLS,
    JOURNEY_BALL_ICONS,
    JOURNEY_PHASE_COLORS,
    journeyStepLabel,
    journeyWaitingDays,
  } from '@/utils/journey';
  import DealOwnerPicker from '@/components/deals/DealOwnerPicker.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { readableTextColor } from '@shared/utils/colorContrast';

  const PAGE_SIZE = 50;
  const LONG_WAIT_DAYS = 14;
  const DEFAULT_PHASE_COLOR = '#434f6f';

  export default {
    name: 'DealJourneyBoard',

    components: { BaseIcon, DealOwnerPicker },

    props: {
      members: {
        type: Array,
        default: () => [],
      },
      // 'deals' follows the deal store's pipeline and filters; 'projects' spans all pipelines
      source: {
        type: String,
        default: 'deals',
        validator: (value) => ['deals', 'projects'].includes(value),
      },
      search: {
        type: String,
        default: '',
      },
      refreshKey: {
        type: [String, Number],
        default: 0,
      },
    },

    emits: ['card-click', 'assign-owner'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const dealStore = useDealStore();

      const isProjectBoard = computed(() => props.source === 'projects');
      const board = ref({ phases: [], deals: [] });
      const loading = ref(false);
      const ballFilter = ref('all');
      const limits = ref({});
      let requestId = 0;

      const loadDealBoard = () => {
        const filters = dealStore.filters || {};
        const params = {
          pipeline_id: dealStore.currentPipelineId,
          owner_id: filters.owner_id || undefined,
          search: filters.search || undefined,
        };
        if (Array.isArray(filters.tag_ids) && filters.tag_ids.length) {
          params.tag_ids = filters.tag_ids.join(',');
        }
        return dealService.getJourneyBoard(params);
      };

      const loadProjectBoard = () =>
        projectService.getJourneyBoard({ search: props.search.trim() || undefined });

      const fetchBoard = async () => {
        if (!isProjectBoard.value && !dealStore.currentPipelineId) return;

        const current = ++requestId;
        loading.value = true;
        try {
          const data = await (isProjectBoard.value ? loadProjectBoard() : loadDealBoard());
          if (current === requestId) board.value = data;
        } catch (error) {
          if (current === requestId) board.value = { phases: [], deals: [] };
        } finally {
          if (current === requestId) loading.value = false;
        }
      };

      // The deals page refetches the kanban on every filter change and deal update; follow along.
      watch(
        () =>
          isProjectBoard.value
            ? [props.search, props.refreshKey]
            : [dealStore.currentPipelineId, dealStore.kanbanData, props.refreshKey],
        fetchBoard,
        { immediate: true }
      );

      const waitingDays = (deal) => journeyWaitingDays(deal.journey.waiting_since);

      const filteredDeals = computed(() =>
        ballFilter.value === 'all'
          ? board.value.deals
          : board.value.deals.filter((d) => d.journey.ball === ballFilter.value)
      );

      const sortDeals = (deals) =>
        [...deals].sort((a, b) => {
          const aUs = a.journey.ball === 'us' ? 0 : 1;
          const bUs = b.journey.ball === 'us' ? 0 : 1;
          if (aUs !== bUs) return aUs - bUs;
          return waitingDays(b) - waitingDays(a);
        });

      const columns = computed(() =>
        board.value.phases.map((key) => {
          const deals = sortDeals(filteredDeals.value.filter((d) => d.journey.phase === key));
          const limit = limits.value[key] || PAGE_SIZE;
          const color = JOURNEY_PHASE_COLORS[key] || DEFAULT_PHASE_COLOR;
          return {
            key,
            deals,
            visibleDeals: deals.slice(0, limit),
            value: deals.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
            color,
            textColor: readableTextColor(color, { dark: '#18181b' }),
            // `14` is an 8% alpha channel, matching the kanban column tint
            background: `${color}14`,
          };
        })
      );

      const ballOptions = computed(() => {
        const counts = {};
        for (const deal of board.value.deals) {
          counts[deal.journey.ball] = (counts[deal.journey.ball] || 0) + 1;
        }
        return [
          { value: 'all', label: $t('journey.board.all'), count: board.value.deals.length },
          ...JOURNEY_BALLS.map((ball) => ({
            value: ball,
            icon: JOURNEY_BALL_ICONS[ball],
            label: $t(`journey.ball.${ball}`),
            count: counts[ball] || 0,
          })),
        ];
      });

      const showMore = (key) => {
        limits.value = { ...limits.value, [key]: (limits.value[key] || PAGE_SIZE) + PAGE_SIZE };
      };

      const dealName = (deal) => {
        const lead = deal.lead;
        const name = lead
          ? [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
            lead.name ||
            lead.company_name
          : null;
        return name || deal.title || $t('deals.untitled_deal');
      };
      const signalTone = (deal) => {
        if (deal.journey.ball !== 'us') return 'neutral';
        return waitingDays(deal) > LONG_WAIT_DAYS ? 'warning' : 'info';
      };

      const stepLabel = (step) => journeyStepLabel($t, step);

      const formatCurrency = (value) =>
        new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);

      return {
        isProjectBoard,
        loading,
        ballFilter,
        ballOptions,
        columns,
        ballIcons: JOURNEY_BALL_ICONS,
        showMore,
        dealName,
        signalTone,
        stepLabel,
        waitingDays,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .journey-board {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: calc(100vh - 192px);
    @include lg-down {
      height: calc(100vh - 320px);
    }

    &__toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    &__filters {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      padding: 3px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      background: #fff;
    }

    &__filter {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      padding: 4px 8px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #434f6f;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      cursor: pointer;

      @include hover {
        background: rgb(0 9 33 / 4%);
      }

      &:focus-visible {
        outline: 2px solid #2f69ff;
        outline-offset: 2px;
      }

      &--active {
        background: #e3e6ee;
        color: #000921;

        @include hover {
          background: #e3e6ee;
        }
      }
    }

    &__filter-count {
      color: #8a93a8;
      font-size: 11px;
      font-weight: 500;
    }

    &__loading {
      color: #8a93a8;
      font-size: 13px;
      font-weight: 300;
    }

    &__scroller {
      flex: 1;
      min-height: 0;
      overflow-x: auto;
      padding-bottom: 0.5rem;
      @include lg-down {
        margin: 0 -1rem;
        padding-left: 1rem;
      }
    }

    &__columns {
      display: flex;
      gap: 16px;
      height: 100%;
      width: max-content;
      min-width: 100%;
    }

    &__column {
      flex: 0 0 308px;
      width: 308px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      height: 100%;
      min-height: 0;
      padding: 10px;
      border-radius: 6px;
    }

    &__column-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    &__column-title {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    &__phase-badge {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      height: 24px;
      padding: 0 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
      white-space: nowrap;
    }

    &__column-value,
    &__column-count {
      flex-shrink: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
    }

    &__column-count {
      font-weight: 300;
    }

    &__body {
      position: relative;
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }

    &__list {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 4px;
      min-height: 80px;
      overflow-y: auto;
    }

    &__card {
      display: block;
      width: 100%;
      padding: 14px 16px 12px;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;
      font: inherit;
      text-align: left;
      cursor: pointer;

      @include hover {
        border-color: #d7d7d7;
        box-shadow: 0 2px 4px rgba(0, 9, 33, 0.06);
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 2px;
      }
    }

    &__heading {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 16px;
    }

    &__name,
    &__amount {
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      line-height: normal;
      white-space: nowrap;
    }

    &__name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__amount {
      flex-shrink: 0;
    }

    &__status {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 30px;
      padding-top: 10px;
    }

    &__signal {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 4px;
      min-width: 0;
      color: #434f6f;

      :deep(svg) {
        flex-shrink: 0;
      }

      &--info {
        color: #3364e6;
      }

      &--warning {
        color: #ff9500;
      }
    }

    &__signal-text {
      overflow: hidden;
      font-size: 11px;
      font-weight: 300;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__stage {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      padding-top: 2px;
      color: #8a93a8;
      font-size: 11px;
      font-weight: 300;
      line-height: 16px;
    }

    &__stage-dot {
      flex-shrink: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    &__stage-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__more {
      flex-shrink: 0;
      padding: 8px;
      border: 1px dashed #d7d7d7;
      border-radius: 4px;
      background: transparent;
      color: #434f6f;
      font: inherit;
      font-size: 13px;
      font-weight: 300;
      cursor: pointer;

      @include hover {
        background: rgb(0 9 33 / 4%);
      }
    }

    &__empty {
      position: absolute;
      top: 8px;
      right: 0;
      left: 0;
      margin: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
      line-height: 20px;
      text-align: center;
      pointer-events: none;
    }
  }
</style>
